import { T, useGT } from 'gt-react';
import { CategorySortSchema, type CategorySort } from '@erp/shared-types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { ConfigRow, ConfigSelectTrigger } from './shared';

const TOP_N = [0, 5, 10, 20, 50] as const;

/** A category chart's Sort and, where it has one, Top N. */
export function CategorySortFields({
  idPrefix,
  sort,
  topN,
  unsortedLabel,
  onChange,
}: {
  idPrefix: string;
  sort: CategorySort;
  /** Absent for a chart that keeps every category. */
  topN?: number;
  /** What no sort means for this chart; by default, as the rows appear. */
  unsortedLabel?: string;
  onChange: (next: { sort: CategorySort; topN?: number }) => void;
}) {
  const gt = useGT();
  return (
    <div className="flex gap-2">
      <div className="min-w-0 flex-1">
        <ConfigRow
          label={gt('Sort', { context: 'chart category sort' })}
          htmlFor={`${idPrefix}-sort`}
        >
          <Select
            value={sort}
            onValueChange={(next) =>
              onChange({ sort: CategorySortSchema.parse(next), topN })
            }
          >
            <ConfigSelectTrigger id={`${idPrefix}-sort`}>
              <SelectValue />
            </ConfigSelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                {unsortedLabel ?? (
                  <T context="chart category sort">As they appear</T>
                )}
              </SelectItem>
              <SelectItem value="value-desc">
                <T context="chart category sort">Highest value first</T>
              </SelectItem>
              <SelectItem value="value-asc">
                <T context="chart category sort">Lowest value first</T>
              </SelectItem>
              <SelectItem value="label-asc">
                <T context="chart category sort">Label A to Z</T>
              </SelectItem>
              <SelectItem value="label-desc">
                <T context="chart category sort">Label Z to A</T>
              </SelectItem>
            </SelectContent>
          </Select>
        </ConfigRow>
      </div>
      {topN !== undefined && (
        <div className="w-28">
          <ConfigRow
            label={gt('Show', { context: 'chart top N' })}
            htmlFor={`${idPrefix}-top`}
          >
            <Select
              value={String(topN)}
              onValueChange={(next) => onChange({ sort, topN: Number(next) })}
            >
              <ConfigSelectTrigger id={`${idPrefix}-top`}>
                <SelectValue />
              </ConfigSelectTrigger>
              <SelectContent>
                {TOP_N.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n === 0
                      ? gt('All', { context: 'chart top N' })
                      : gt('First {n}', { n, context: 'chart top N' })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ConfigRow>
        </div>
      )}
    </div>
  );
}
